// Opt-in, audio-only WebRTC mesh for one camp (at most eight couriers). Kept outside React so
// media, negotiation, recovery and cancellation have one lifetime across the lobby and the trail.
//
// Ported from the sibling secret-hitler app. Its reliability model is unchanged:
//   peer: connecting → connected → recovering(n) → failed
//   - 'failed', or a lingering 'disconnected', triggers restartIce() plus a fresh offer from the
//     deterministic offerer, with 1s/2s/4s backoff, three attempts, then a visible Reconnect.
//   - a transport reconnect or a tab return re-joins the call if the courier had opted in.
//   - devicechange / track end re-acquire the default microphone and replaceTrack everywhere.
//   - speaking levels come from one AudioContext analyser per track.
//
// The one adaptation: hearthhop speaks a raw WebSocket protocol with no request/ack, so a
// request here is a send plus a bounded wait for the frame that answers it (see `ask`).
import type {
  ClientMessage, VoiceIceServer, VoiceParticipant, VoiceSignal, VoiceSignalMessage, VoiceState,
} from '../shared/types';
import type { VoiceEvent, VoiceListener } from './network';

export type Connection = 'connecting' | 'connected' | 'recovering' | 'failed';
export interface VoiceSnapshot {
  status: 'off' | 'joining' | 'joined';
  ready: boolean;
  enabled: boolean;
  unsupported: string | null;
  muted: boolean;
  busy: boolean;
  participants: VoiceParticipant[];
  connections: Record<string, Connection>;
  /** Couriers whose audio level is above the speaking threshold right now (including you). */
  speaking: Record<string, boolean>;
  /** 'none' when no TURN relay is configured: peers behind symmetric NAT cannot connect. */
  relay: 'unknown' | 'none' | 'configured';
  /** True while an automatic re-join is pending after a transport or tab interruption. */
  rejoining: boolean;
  /** Whether the courier currently wants a microphone (false after muting or joining to listen). */
  intentMicrophone: boolean;
  /** True once the microphone was on in this call: a muted mic is "Muted", never "Listen only". */
  everSpoke: boolean;
  playbackBlocked: boolean;
  error: string | null;
}

export const emptyVoiceSnapshot: VoiceSnapshot = {
  status: 'off', ready: false, enabled: true, unsupported: null, muted: true,
  busy: false, participants: [], connections: {}, speaking: {}, relay: 'unknown',
  rejoining: false, intentMicrophone: false, everSpoke: false, playbackBlocked: false, error: null,
};

export const RECOVERY_BACKOFF_MS = [1000, 2000, 4000];
export const DISCONNECTED_GRACE_MS = 2500;
export const CONNECT_TIMEOUT_MS = 15_000;
/** How long a request waits for the frame that answers it before giving up. */
export const REQUEST_TIMEOUT_MS = 8_000;
export const SPEAKING_THRESHOLD = 0.02;
export const SPEAKING_INTERVAL_MS = 80;
/** Keep the ring lit briefly between syllables so it reads as speech, not flicker. */
export const SPEAKING_HOLD_MS = 600;

interface Peer {
  sessionId: string;
  pc: RTCPeerConnection;
  sender: RTCRtpSender | null;
  audio: HTMLAudioElement | null;
  blocked: boolean;
  connection: Connection;
  attempts: number;
  queue: Promise<void>;
  candidates: RTCIceCandidateInit[];
  timeout: ReturnType<typeof setTimeout> | null;
  retryTimer: ReturnType<typeof setTimeout> | null;
  makingOffer: boolean;
  offerer: boolean;
  analyser: { context: AudioContext; node: AnalyserNode; source: MediaStreamAudioSourceNode } | null;
}

export function microphoneError(error: unknown): string {
  if (error instanceof Error) {
    if (error.name === 'NotAllowedError' || error.name === 'SecurityError') return 'Microphone access was denied. Allow it in your browser’s site settings, try again, or choose Listen only.';
    if (error.name === 'NotFoundError') return 'No microphone was found. Connect one, or choose Listen only.';
    if (error.name === 'NotReadableError') return 'Your microphone is unavailable or in use. Check your device, or choose Listen only.';
    return error.message;
  }
  return 'Voice chat could not connect. Please try again.';
}

/** A TURN relay is what lets phones on carrier NAT connect. */
export function relayConfigured(iceServers: VoiceIceServer[] | null): VoiceSnapshot['relay'] {
  if (iceServers === null) return 'unknown';
  const urls = iceServers.flatMap((server) => Array.isArray(server.urls) ? server.urls : [server.urls]);
  return urls.some((url) => /^turns?:/i.test(url)) ? 'configured' : 'none';
}

/** What the controller needs from the room's transport. */
export interface VoiceTransport {
  send(message: ClientMessage): void;
  subscribe(listener: VoiceListener): () => void;
  /** Whether the room currently has a live, authenticated socket. */
  online(): boolean;
}

export class VoiceClient {
  private state: VoiceState;
  private status: VoiceSnapshot['status'] = 'off';
  private sessionId: string | null = null;
  private iceServers: VoiceIceServer[] | null = null;
  private stream: MediaStream | null = null;
  private microphoneGeneration = 0;
  private peers = new Map<string, Peer>();
  private pendingSignals: VoiceSignalMessage[] = [];
  private muted = true;
  private busy = false;
  private error: string | null = null;
  private disposed = false;
  private unsupported: string | null;
  private unsubscribe: (() => void) | null = null;
  /** Requests waiting for the frame that answers them. */
  private waiting = new Set<(event: VoiceEvent) => void>();
  /** What the courier asked for last, so an interruption (or a reload, per tab) can be undone. */
  private intent: { microphone: boolean } | null = null;
  private get intentKey(): string { return `hearthhop.voice.intent:${this.room.code}:${this.room.playerId}`; }
  private rememberIntent(intent: { microphone: boolean } | null): void {
    this.intent = intent;
    try {
      if (intent) sessionStorage.setItem(this.intentKey, JSON.stringify({ ...intent, spoke: this.everSpoke }));
      else sessionStorage.removeItem(this.intentKey);
    } catch { /* memory only */ }
  }
  private recallIntent(): { microphone: boolean } | null {
    try {
      const raw = sessionStorage.getItem(this.intentKey);
      const parsed = raw ? JSON.parse(raw) as { microphone?: unknown; spoke?: unknown } : null;
      if (!parsed || typeof parsed.microphone !== 'boolean') return null;
      // A muted microphone stays "Muted" (not "Listen only") across a reload.
      if (parsed.spoke === true) this.everSpoke = true;
      return { microphone: parsed.microphone };
    } catch { return null; }
  }
  private rejoinTimer: ReturnType<typeof setTimeout> | null = null;
  private rejoining = false;
  private speaking = new Map<string, boolean>();
  private lastLoud = new Map<string, number>();
  private everSpoke = false;
  private speakingTimer: ReturnType<typeof setInterval> | null = null;
  private localAnalyser: Peer['analyser'] = null;

  constructor(
    private transport: VoiceTransport,
    private room: { code: string; playerId: string },
    private publish: (snapshot: VoiceSnapshot) => void,
  ) {
    this.state = { code: room.code, revision: -1, enabled: true, participants: [] };
    this.unsupported = !window.isSecureContext ? 'Voice chat needs HTTPS or localhost.'
      : !navigator.mediaDevices?.getUserMedia || typeof RTCPeerConnection === 'undefined'
        ? 'This browser does not support voice chat. Try a recent Chrome, Firefox, Edge, or Safari.' : null;
  }

  private get me(): VoiceParticipant | undefined {
    return this.state.participants.find((p) => p.playerId === this.room.playerId && p.sessionId === this.sessionId);
  }

  private update(): void {
    if (this.disposed) return;
    this.publish({
      status: this.status, ready: this.transport.online() && this.state.revision >= 0,
      enabled: this.state.enabled, unsupported: this.unsupported, muted: this.muted,
      busy: this.busy, participants: this.state.participants,
      connections: Object.fromEntries([...this.peers].map(([id, peer]) => [id, peer.connection])),
      speaking: Object.fromEntries(this.speaking),
      relay: relayConfigured(this.iceServers),
      rejoining: this.rejoining,
      intentMicrophone: this.intent?.microphone ?? false,
      everSpoke: this.everSpoke,
      playbackBlocked: [...this.peers.values()].some((peer) => peer.blocked), error: this.error,
    });
  }

  // --- requests ------------------------------------------------------------------
  // There is no ack on this wire: send, then wait for the frame that answers, or for the
  // service's refusal. A request never blocks the game protocol and never retries itself.

  private ask<T>(message: ClientMessage, answer: (event: VoiceEvent) => T | undefined): Promise<T> {
    return new Promise<T>((accept, reject) => {
      if (!this.transport.online()) { reject(new Error('Voice needs a live connection to your camp.')); return; }
      const settle = (event: VoiceEvent) => {
        if (event.type === 'voice-error') { finish(); reject(new Error(event.message)); return; }
        if (event.type === 'voice-transport' && !event.online) { finish(); reject(new Error('The connection to your camp dropped.')); return; }
        const value = answer(event);
        if (value !== undefined) { finish(); accept(value); }
      };
      const timer = setTimeout(() => { finish(); reject(new Error('Voice chat did not answer. Please try again.')); }, REQUEST_TIMEOUT_MS);
      const finish = () => { clearTimeout(timer); this.waiting.delete(settle); };
      this.waiting.add(settle);
      this.transport.send(message);
    });
  }

  /** Fire and forget: the service confirms through 'voice-state', and refuses through an error. */
  private tell(message: ClientMessage): void {
    if (this.transport.online()) this.transport.send(message);
  }

  private onEvent = (event: VoiceEvent): void => {
    if (this.disposed) return;
    for (const settle of [...this.waiting]) settle(event);
    if (event.type === 'voice-state') this.onState(event.state);
    else if (event.type === 'voice-signal') this.onSignal(event.message);
    else if (event.type === 'voice-transport') { if (event.online) this.onReconnect(); else this.onDisconnect(); }
    else if (event.type === 'voice-error' && this.sessionId && !this.busy && this.status === 'joined') {
      // A refusal outside a request we are awaiting (a relayed signal, a late mute).
      this.error = event.message;
      this.update();
    }
  };

  start(): void {
    this.unsubscribe = this.transport.subscribe(this.onEvent);
    // The roster the service pushed with the welcome predates this controller, so ask for it.
    this.tell({ type: 'voice-sync' });
    window.addEventListener('pagehide', this.onPageHide);
    document.addEventListener('visibilitychange', this.onVisibility);
    navigator.mediaDevices?.addEventListener?.('devicechange', this.onDeviceChange);
    // A reload in a tab that was in the call rejoins it (same opt-in, same mic choice).
    const remembered = this.recallIntent();
    if (remembered) { this.intent = remembered; this.scheduleRejoin(300); }
    this.update();
  }

  dispose(): void {
    this.disposed = true;
    void this.analyserContext?.close().catch(() => undefined);
    this.analyserContext = null;
    this.unsubscribe?.();
    this.unsubscribe = null;
    this.waiting.clear();
    window.removeEventListener('pagehide', this.onPageHide);
    document.removeEventListener('visibilitychange', this.onVisibility);
    navigator.mediaDevices?.removeEventListener?.('devicechange', this.onDeviceChange);
    this.cancelRejoin();
    this.end(true);
    this.error = null;
  }

  // --- interruptions and automatic recovery ---------------------------------

  private onDisconnect = (): void => {
    const wasActive = this.sessionId !== null;
    this.end(false);
    this.state = { ...this.state, revision: -1, participants: [] };
    if (wasActive && this.intent) {
      this.rejoining = true;
      this.error = null;
    } else {
      this.error = wasActive ? 'Voice disconnected. Join again when your camp reconnects.' : null;
    }
    this.update();
  };

  /** The seat is re-bound by the room hook once it is online; voice state follows, then we re-join. */
  private onReconnect = (): void => {
    // The resumed socket carries a fresh roster push, but asking makes the order certain.
    this.tell({ type: 'voice-sync' });
    if (this.intent) this.scheduleRejoin(400);
  };

  private onVisibility = (): void => {
    if (document.visibilityState !== 'visible') return;
    if (this.intent && !this.sessionId) this.scheduleRejoin(150);
    else if (this.sessionId) {
      // Peers that silently died while the tab slept get a nudge.
      for (const [id, peer] of this.peers) {
        if (peer.pc.connectionState === 'failed' || peer.pc.connectionState === 'disconnected') this.recover(id, peer);
      }
      void this.resumeAudio();
    }
  };

  private scheduleRejoin(delay: number): void {
    if (this.disposed || !this.intent) return;
    this.cancelRejoin();
    this.rejoining = true;
    this.update();
    this.rejoinTimer = setTimeout(() => {
      this.rejoinTimer = null;
      if (!this.intent || this.sessionId || this.disposed) { this.rejoining = false; this.update(); return; }
      if (!this.transport.online() || this.state.revision < 0) {
        // No roster yet: ask again rather than waiting for a push that may already have gone out.
        this.tell({ type: 'voice-sync' });
        this.scheduleRejoin(600);
        return;
      }
      void this.join(this.intent.microphone, true).finally(() => { this.rejoining = false; this.update(); });
    }, delay);
  }

  private cancelRejoin(): void {
    if (this.rejoinTimer) clearTimeout(this.rejoinTimer);
    this.rejoinTimer = null;
    this.rejoining = false;
  }

  /** Closing/reloading the tab stops the microphone; the per-tab intent survives for a reload. */
  private onPageHide = (): void => { this.cancelRejoin(); this.end(true); this.error = null; this.update(); };

  private onDeviceChange = (): void => {
    // Only matters while we are sending: re-acquire the (new) default microphone.
    if (!this.sessionId || !this.stream) return;
    void this.reacquireMicrophone('Your microphone changed; switching to the new device.');
  };

  private onState = (state: VoiceState): void => {
    if (state.code !== this.room.code || state.revision < this.state.revision) return;
    this.state = state;
    if (this.sessionId && this.iceServers !== null) {
      if (!this.me || !state.enabled) {
        this.end(false);
        this.error = 'Voice chat ended. You can join again when it is available.';
      } else {
        if (this.me.muted) {
          this.muted = true;
          this.stream?.getAudioTracks().forEach((track) => { track.enabled = false; });
        }
        try { this.reconcilePeers(); }
        catch (error) {
          this.end(true);
          this.error = microphoneError(error);
        }
      }
    }
    this.update();
  };

  // --- joining and leaving ----------------------------------------------------

  async join(withMicrophone = true, automatic = false): Promise<void> {
    if (this.sessionId || this.disposed || !this.transport.online() || this.state.revision < 0
      || !this.state.enabled || this.unsupported) return;
    if (!automatic) this.rememberIntent({ microphone: withMicrophone });
    const sessionId = crypto.randomUUID();
    this.sessionId = sessionId;
    this.status = 'joining';
    this.error = null;
    this.update();
    try {
      const iceServers = await this.ask<VoiceIceServer[]>({ type: 'voice-join', sessionId },
        (event) => event.type === 'voice-ready' && event.sessionId === sessionId ? event.iceServers : undefined);
      if (this.sessionId !== sessionId) return;
      this.iceServers = iceServers;
      // The roster that named us arrived with (or just before) the relay list; act on it now
      // that the peers can actually be built.
      this.onState(this.state);
      if (this.sessionId !== sessionId) return;
      for (const signal of this.pendingSignals.splice(0)) this.onSignal(signal);
      if (withMicrophone) await this.enableMicrophone(sessionId);
      if (this.sessionId !== sessionId) return;
      this.status = 'joined';
      this.startSpeakingMeter();
      this.update();
    } catch (error) {
      if (this.sessionId !== sessionId) return;
      this.end(true);
      this.error = microphoneError(error);
      if (!automatic) this.rememberIntent(null);
      this.update();
    }
  }

  leave(): void {
    this.rememberIntent(null);
    this.cancelRejoin();
    this.end(true);
    this.error = null;
    this.update();
  }

  /** Tear down and rebuild the whole call: the manual escape hatch once automatic recovery gave up. */
  async reconnect(): Promise<void> {
    const microphone = this.intent?.microphone ?? !this.muted;
    this.end(true);
    this.error = null;
    this.update();
    await this.join(microphone);
  }

  private end(notify: boolean): void {
    const sessionId = this.sessionId;
    this.sessionId = null;
    this.status = 'off';
    this.busy = false;
    if (!this.intent) this.everSpoke = false; // an interruption keeps the wording; a leave resets it
    this.iceServers = null;
    this.pendingSignals = [];
    this.stopMicrophone();
    this.stopSpeakingMeter();
    for (const id of [...this.peers.keys()]) this.closePeer(id);
    if (notify && sessionId) this.tell({ type: 'voice-leave', sessionId });
  }

  // --- microphone ---------------------------------------------------------------

  private stopMicrophone(): void {
    this.microphoneGeneration += 1;
    this.muted = true;
    this.stream?.getTracks().forEach((track) => { track.onended = null; track.stop(); });
    this.stream = null;
    this.detachLocalAnalyser();
    for (const peer of this.peers.values()) void this.safeReplaceTrack(peer, null);
  }

  /** replaceTrack must never be handed an ended track, and a failure must not kill the peer. */
  private async safeReplaceTrack(peer: Peer, track: MediaStreamTrack | null): Promise<boolean> {
    if (!peer.sender) return false;
    if (track && track.readyState === 'ended') track = null;
    try { await peer.sender.replaceTrack(track); return true; }
    catch { return false; }
  }

  private async acquireMicrophone(): Promise<MediaStream> {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true }, video: false,
    });
    stream.getTracks().forEach((track) => { track.enabled = false; });
    return stream;
  }

  /** Ask the service to publish a microphone state, and wait for the roster that confirms it. */
  private async setMuted(sessionId: string, muted: boolean): Promise<void> {
    await this.ask<true>({ type: 'voice-mute', sessionId, muted }, (event) =>
      event.type === 'voice-state' && event.state.participants.some(
        (p) => p.playerId === this.room.playerId && p.sessionId === sessionId && p.muted === muted) ? true : undefined);
  }

  private async enableMicrophone(sessionId: string): Promise<void> {
    const generation = this.microphoneGeneration;
    if (!this.stream) {
      const stream = await this.acquireMicrophone();
      // getUserMedia is not abortable. A late grant must never revive a call.
      if (this.sessionId !== sessionId || this.microphoneGeneration !== generation) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }
      const track = stream.getAudioTracks()[0];
      if (!track || track.readyState === 'ended') {
        stream.getTracks().forEach((value) => value.stop());
        throw new Error('No working microphone was found. Connect one, or choose Listen only.');
      }
      this.stream = stream;
      this.watchTrack(track, sessionId);
      this.attachLocalAnalyser(stream);
      await Promise.all([...this.peers.values()].map((peer) => this.safeReplaceTrack(peer, track)));
    }
    if (this.sessionId !== sessionId || this.microphoneGeneration !== generation || !this.stream) return;
    await this.setMuted(sessionId, false);
    if (this.sessionId !== sessionId || this.microphoneGeneration !== generation || !this.stream) return;
    this.muted = false;
    this.everSpoke = true;
    if (this.intent) this.rememberIntent(this.intent); // persist "spoke" for a reload
    this.stream.getAudioTracks().forEach((track) => { track.enabled = true; });
  }

  private watchTrack(track: MediaStreamTrack, sessionId: string): void {
    track.onended = () => {
      if (this.sessionId !== sessionId || this.stream?.getAudioTracks()[0] !== track) return;
      // Headphones off, USB mic unplugged: try the new default device before giving up.
      void this.reacquireMicrophone('Your microphone disconnected; switching to the available device.');
    };
  }

  /** Swap to the current default microphone, keeping the mute state the courier chose. */
  private async reacquireMicrophone(notice: string): Promise<void> {
    const sessionId = this.sessionId;
    if (!sessionId) return;
    const wasMuted = this.muted;
    const generation = ++this.microphoneGeneration;
    const old = this.stream;
    this.stream = null;
    this.detachLocalAnalyser();
    old?.getTracks().forEach((track) => { track.onended = null; track.stop(); });
    try {
      const stream = await this.acquireMicrophone();
      if (this.sessionId !== sessionId || this.microphoneGeneration !== generation) { stream.getTracks().forEach((track) => track.stop()); return; }
      const track = stream.getAudioTracks()[0];
      if (!track || track.readyState === 'ended') throw new Error('No working microphone was found.');
      this.stream = stream;
      this.watchTrack(track, sessionId);
      this.attachLocalAnalyser(stream);
      track.enabled = !wasMuted;
      await Promise.all([...this.peers.values()].map((peer) => this.safeReplaceTrack(peer, track)));
      this.error = null;
    } catch (error) {
      if (this.sessionId !== sessionId) return;
      this.muted = true;
      this.error = `${notice} ${microphoneError(error)}`;
      this.tell({ type: 'voice-mute', sessionId, muted: true });
    }
    this.update();
  }

  async toggleMute(): Promise<void> {
    const sessionId = this.sessionId;
    if (!sessionId || this.status !== 'joined' || this.busy) return;
    this.busy = true;
    this.error = null;
    try {
      if (this.muted) {
        this.update();
        await this.enableMicrophone(sessionId);
        if (this.intent) this.rememberIntent({ microphone: true });
      } else {
        // Local silence is immediate; it never waits on a network round-trip.
        this.muted = true;
        this.stream?.getAudioTracks().forEach((track) => { track.enabled = false; });
        if (this.intent) this.rememberIntent({ microphone: false });
        this.update();
        await this.setMuted(sessionId, true);
      }
    } catch (error) {
      if (this.sessionId === sessionId) this.error = microphoneError(error);
    } finally {
      if (this.sessionId === sessionId) { this.busy = false; this.update(); }
    }
  }

  // --- peers ---------------------------------------------------------------------

  private reconcilePeers(): void {
    for (const [id, peer] of this.peers) {
      if (!this.state.participants.some((p) => p.playerId === id && p.sessionId === peer.sessionId)) this.closePeer(id);
    }
    for (const participant of this.state.participants) {
      if (participant.playerId === this.room.playerId) continue;
      const peer = this.peers.get(participant.playerId) ?? this.createPeer(participant);
      // Enforce the authoritative mute state at the receiving end too.
      if (peer.audio) {
        const wasMuted = peer.audio.muted;
        peer.audio.muted = participant.muted;
        if (wasMuted && !peer.audio.muted) void this.playAudio(participant.playerId, peer);
      }
    }
  }

  private createPeer(participant: VoiceParticipant): Peer {
    const id = participant.playerId;
    const pc = new RTCPeerConnection({ iceServers: this.iceServers ?? [], bundlePolicy: 'max-bundle' });
    // Exactly one offerer per pair (lower id) prevents glare on simultaneous joins and restarts.
    const offerer = this.room.playerId < id;
    let sender: RTCRtpSender | null = null;
    try { if (offerer) sender = pc.addTransceiver('audio', { direction: 'sendrecv' }).sender; }
    catch (error) { pc.close(); throw error; }
    const peer: Peer = {
      sessionId: participant.sessionId, pc, sender, audio: null, blocked: false,
      connection: 'connecting', attempts: 0, queue: Promise.resolve(), candidates: [], timeout: null, retryTimer: null,
      makingOffer: false, offerer, analyser: null,
    };
    this.peers.set(id, peer);
    this.watchConnection(id, peer);
    pc.onicecandidate = (event) => {
      if (event.candidate && this.peers.get(id) === peer) {
        this.sendSignal(id, peer, { type: 'ice', candidate: event.candidate.toJSON() as RTCIceCandidateInit & { candidate: string } });
      }
    };
    pc.onconnectionstatechange = () => {
      if (this.peers.get(id) !== peer) return;
      const state = pc.connectionState;
      if (state === 'connected') {
        peer.connection = 'connected';
        peer.attempts = 0;
        this.clearTimers(peer);
        void this.resumeAudio();
      } else if (state === 'failed') {
        this.recover(id, peer);
      } else if (state === 'disconnected') {
        // Often transient on phones: give ICE a moment, then restart it.
        if (peer.connection === 'connected') peer.connection = 'recovering';
        if (!peer.retryTimer) {
          peer.retryTimer = setTimeout(() => { peer.retryTimer = null; if (pc.connectionState === 'disconnected') this.recover(id, peer); }, DISCONNECTED_GRACE_MS);
        }
      }
      this.update();
    };
    pc.onnegotiationneeded = () => {
      if (!offerer || this.peers.get(id) !== peer) return;
      this.enqueue(id, peer, () => this.offer(id, peer, false));
    };
    pc.ontrack = (event) => {
      if (this.peers.get(id) !== peer || event.track.kind !== 'audio') return;
      const stream = event.streams[0] ?? new MediaStream([event.track]);
      if (!peer.audio) {
        const audio = document.createElement('audio');
        audio.autoplay = true;
        audio.hidden = true;
        audio.setAttribute('playsinline', '');
        audio.dataset.voicePeer = id;
        const current = this.state.participants.find((p) => p.playerId === id);
        audio.muted = !current || current.muted;
        peer.audio = audio;
        document.body.append(audio);
      }
      peer.audio.srcObject = stream;
      this.attachAnalyser(peer, stream);
      void this.playAudio(id, peer);
    };
    this.enqueue(id, peer, async () => {
      if (this.stream) await this.safeReplaceTrack(peer, this.stream.getAudioTracks()[0]);
      if (offerer) await this.offer(id, peer, false);
    });
    return peer;
  }

  private async offer(id: string, peer: Peer, iceRestart: boolean): Promise<void> {
    const { pc } = peer;
    if (peer.makingOffer) return;
    if (pc.signalingState !== 'stable') {
      // A restart while our previous offer is still unanswered: roll it back first.
      if (!iceRestart || pc.signalingState !== 'have-local-offer') return;
      try { await pc.setLocalDescription({ type: 'rollback' }); } catch { return; }
    }
    peer.makingOffer = true;
    try {
      await pc.setLocalDescription(await pc.createOffer(iceRestart ? { iceRestart: true } : undefined));
      if (!pc.localDescription) return;
      this.sendSignal(id, peer, { type: 'offer', sdp: pc.localDescription.sdp });
    } finally {
      peer.makingOffer = false;
    }
  }

  /** ICE restart with bounded backoff; the answerer waits for the offerer's restart. */
  private recover(id: string, peer: Peer): void {
    if (this.peers.get(id) !== peer || !this.sessionId) return;
    if (peer.attempts >= RECOVERY_BACKOFF_MS.length) {
      peer.connection = 'failed';
      this.clearTimers(peer);
      this.update();
      return;
    }
    peer.connection = 'recovering';
    this.update();
    const delay = RECOVERY_BACKOFF_MS[peer.attempts];
    peer.attempts += 1;
    if (peer.retryTimer) clearTimeout(peer.retryTimer);
    peer.retryTimer = setTimeout(() => {
      peer.retryTimer = null;
      if (this.peers.get(id) !== peer || peer.pc.connectionState === 'connected' || peer.pc.connectionState === 'closed') return;
      if (peer.offerer) {
        try { peer.pc.restartIce(); } catch { /* older engines: the iceRestart offer below covers it */ }
        this.enqueue(id, peer, () => this.offer(id, peer, true));
      }
      // Every attempt gets a fresh connect window.
      this.watchConnection(id, peer, true);
    }, delay);
  }

  private watchConnection(id: string, peer: Peer, reset = false): void {
    if (peer.timeout) { if (!reset) return; clearTimeout(peer.timeout); }
    peer.timeout = setTimeout(() => {
      peer.timeout = null;
      if (peer.pc.connectionState !== 'connected') this.recover(id, peer);
    }, CONNECT_TIMEOUT_MS);
  }

  private clearTimers(peer: Peer): void {
    if (peer.timeout) clearTimeout(peer.timeout);
    if (peer.retryTimer) clearTimeout(peer.retryTimer);
    peer.timeout = null;
    peer.retryTimer = null;
  }

  private enqueue(id: string, peer: Peer, work: () => Promise<void>): void {
    peer.queue = peer.queue.then(async () => {
      if (this.peers.get(id) === peer) await work();
    }).catch(() => { if (this.peers.get(id) === peer) this.recover(id, peer); });
  }

  private sendSignal(to: string, peer: Peer, signal: VoiceSignal): void {
    if (!this.sessionId || this.peers.get(to) !== peer) return;
    this.tell({ type: 'voice-signal', sessionId: this.sessionId, to, targetSessionId: peer.sessionId, signal });
  }

  private onSignal = (message: VoiceSignalMessage): void => {
    if (!this.sessionId || message.targetSessionId !== this.sessionId) return;
    if (this.iceServers === null) {
      if (this.pendingSignals.length < 256) this.pendingSignals.push(message);
      return;
    }
    const peer = this.peers.get(message.from);
    if (!peer || peer.sessionId !== message.sessionId) return;
    this.enqueue(message.from, peer, async () => {
      const { pc } = peer;
      const { signal } = message;
      if (signal.type === 'ice') {
        if (pc.remoteDescription) { try { await pc.addIceCandidate(signal.candidate); } catch { /* stale candidate after a restart */ } }
        else if (peer.candidates.length < 128) peer.candidates.push(signal.candidate);
        return;
      }
      // Perfect-negotiation lite: the offerer never accepts offers; the answerer accepts any time.
      if (signal.type === 'offer' && peer.offerer) return;
      if (signal.type === 'answer' && pc.signalingState !== 'have-local-offer') return;
      await pc.setRemoteDescription({ type: signal.type, sdp: signal.sdp });
      for (const candidate of peer.candidates.splice(0)) { try { await pc.addIceCandidate(candidate); } catch { /* ignore */ } }
      if (signal.type === 'offer') {
        const transceiver = pc.getTransceivers().find((t) => t.receiver.track.kind === 'audio');
        if (!transceiver) throw new Error('Voice offer did not contain audio');
        // Reserve sending even for a listener, so a later unmute uses replaceTrack, not renegotiation.
        transceiver.direction = 'sendrecv';
        peer.sender = transceiver.sender;
        await this.safeReplaceTrack(peer, this.stream?.getAudioTracks()[0] ?? null);
        await pc.setLocalDescription(await pc.createAnswer());
        if (pc.localDescription) this.sendSignal(message.from, peer, { type: 'answer', sdp: pc.localDescription.sdp });
        // A restart offer arrived: this peer is recovering again.
        if (peer.connection === 'failed') { peer.connection = 'recovering'; peer.attempts = 0; this.watchConnection(message.from, peer, true); this.update(); }
      }
    });
  };

  private async playAudio(id: string, peer: Peer): Promise<void> {
    try { await peer.audio?.play(); peer.blocked = false; }
    // A superseded play() rejects with AbortError while the element keeps playing: not a block.
    catch { peer.blocked = Boolean(peer.audio?.paused); }
    if (this.peers.get(id) === peer) this.update();
  }

  async resumeAudio(): Promise<void> {
    await Promise.all([...this.peers].map(([id, peer]) => (peer.blocked || peer.audio?.paused) ? this.playAudio(id, peer) : Promise.resolve()));
    // Anything that is audibly playing is by definition not blocked.
    for (const peer of this.peers.values()) if (peer.blocked && peer.audio && !peer.audio.paused) peer.blocked = false;
    this.update();
  }

  private closePeer(id: string): void {
    const peer = this.peers.get(id);
    if (!peer) return;
    this.peers.delete(id);
    this.speaking.delete(id);
    this.lastLoud.delete(id);
    this.clearTimers(peer);
    this.detachAnalyser(peer);
    peer.pc.onicecandidate = null;
    peer.pc.ontrack = null;
    peer.pc.onconnectionstatechange = null;
    peer.pc.onnegotiationneeded = null;
    peer.pc.close();
    if (peer.audio) {
      peer.audio.pause();
      peer.audio.srcObject = null;
      peer.audio.remove();
    }
  }

  // --- speaking meter -----------------------------------------------------------

  private analyserContext: AudioContext | null = null;

  private makeAnalyser(stream: MediaStream): Peer['analyser'] {
    try {
      if (typeof AudioContext === 'undefined') return null;
      this.analyserContext ??= new AudioContext();
      const context = this.analyserContext;
      if (context.state === 'suspended') void context.resume().catch(() => undefined);
      const source = context.createMediaStreamSource(stream);
      const node = context.createAnalyser();
      node.fftSize = 256;
      source.connect(node);
      return { context, node, source };
    } catch { return null; }
  }

  private attachAnalyser(peer: Peer, stream: MediaStream): void {
    this.detachAnalyser(peer);
    peer.analyser = this.makeAnalyser(stream);
  }

  private detachAnalyser(peer: Peer): void {
    try { peer.analyser?.source.disconnect(); } catch { /* already gone */ }
    peer.analyser = null;
  }

  private attachLocalAnalyser(stream: MediaStream): void {
    this.detachLocalAnalyser();
    this.localAnalyser = this.makeAnalyser(stream);
  }

  private detachLocalAnalyser(): void {
    try { this.localAnalyser?.source.disconnect(); } catch { /* already gone */ }
    this.localAnalyser = null;
    this.speaking.delete(this.room.playerId);
  }

  private startSpeakingMeter(): void {
    if (this.speakingTimer) return;
    this.speakingTimer = setInterval(() => this.sampleSpeaking(), SPEAKING_INTERVAL_MS);
  }

  private stopSpeakingMeter(): void {
    if (this.speakingTimer) clearInterval(this.speakingTimer);
    this.speakingTimer = null;
    if (this.speaking.size) { this.speaking.clear(); this.update(); }
  }

  /** RMS of the time-domain signal; above threshold = speaking. Hidden tabs skip sampling. */
  private sampleSpeaking(): void {
    if (document.visibilityState !== 'visible') return;
    let changed = false;
    const level = (analyser: Peer['analyser']): number => {
      if (!analyser) return 0;
      const data = new Uint8Array(analyser.node.fftSize);
      analyser.node.getByteTimeDomainData(data);
      let sum = 0;
      for (const value of data) { const centred = (value - 128) / 128; sum += centred * centred; }
      return Math.sqrt(sum / data.length);
    };
    const now = Date.now();
    const set = (id: string, loud: boolean, allowed: boolean) => {
      if (loud && allowed) this.lastLoud.set(id, now);
      const on = allowed && now - (this.lastLoud.get(id) ?? 0) < SPEAKING_HOLD_MS;
      if ((this.speaking.get(id) ?? false) !== on) { if (on) this.speaking.set(id, true); else this.speaking.delete(id); changed = true; }
    };
    set(this.room.playerId, level(this.localAnalyser) > SPEAKING_THRESHOLD, !this.muted);
    for (const [id, peer] of this.peers) {
      const participant = this.state.participants.find((p) => p.playerId === id);
      set(id, level(peer.analyser) > SPEAKING_THRESHOLD, Boolean(participant && !participant.muted));
    }
    if (changed) this.update();
  }
}
